/** @forge-header
 * @id          kalshiApiClient
 * @role        Auth + transport layer | relay routing, retry with backoff, CORS detection
 * @pattern     adapter
 * @purity      api
 * @units       n/a
 * @upstream    kalshiAuth:buildAuthHeaders,getBaseUrl,initAuth | relayHttp:relayHttp,createRelayRequest
 * @downstream  markets | orderbook | orders | positions (internal)
 * @exports     initializeAuth:fn | authenticatedFetch:fn | isCorsError:fn
 * @test        untested (integration-level)
 */

import type { Environment } from '../../types';
import { buildAuthHeaders, getBaseUrl, initAuth } from '../kalshiAuth';
import { relayHttp, createRelayRequest } from '../relayHttp';

interface AuthState {
  accessKeyId: string;
  privateKey: CryptoKey;
  environment: Environment;
  useRelay: boolean;
}

let authState: AuthState | null = null;

/**
 * Initialize auth state. Called by createKalshiClient in index.ts.
 */
export async function initializeAuth(
  accessKeyId: string,
  privateKeyPem: string,
  environment: Environment,
  useRelay: boolean = true
): Promise<void> {
  const { accessKeyId: keyId, privateKey } = await initAuth(accessKeyId, privateKeyPem);
  authState = { accessKeyId: keyId, privateKey, environment, useRelay };
}

/**
 * Make authenticated request through relay with retry logic
 */
export async function authenticatedFetch(
  method: string,
  path: string,
  query: Record<string, string> = {},
  body?: unknown
): Promise<Response> {
  if (!authState) {
    throw new Error('API client not initialized');
  }

  const baseUrl = getBaseUrl(authState.environment);
  const queryString = new URLSearchParams(query).toString();
  const fullPath = queryString ? `${path}?${queryString}` : path;
  const url = `${baseUrl}${fullPath}`;

  const bodyStr = body ? JSON.stringify(body) : '';
  const headers = await buildAuthHeaders(
    authState.accessKeyId,
    authState.privateKey,
    method,
    // Kalshi signature uses the path without query params (per docs).
    path,
    '',
    bodyStr
  );

  const requestHeaders: Record<string, string> = { ...headers };

  // Per Kalshi docs, only include Content-Type when there is a body.
  if (bodyStr) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  // Retry with exponential backoff
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      if (authState.useRelay) {
        // Use relay server
        const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const envelope = createRelayRequest(
          requestId,
          method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
          url,
          requestHeaders,
          bodyStr || undefined
        );

        // Forward through relay
        const relayResponse = await relayHttp(envelope);

        // Convert relay response to Response-like object
        if (relayResponse.status >= 400) {
          const bodyPreview =
            typeof relayResponse.body === 'string' && relayResponse.body.length > 2000
              ? `${relayResponse.body.slice(0, 2000)}…`
              : relayResponse.body;
          throw new Error(`API error: ${relayResponse.status} ${bodyPreview}`);
        }

        // Return a Response-like object
        return new Response(relayResponse.body, {
          status: relayResponse.status,
          statusText: relayResponse.status >= 200 && relayResponse.status < 300 ? 'OK' : 'Error',
          headers: new Headers(relayResponse.headers),
        });
      } else {
        // Direct connection to Kalshi API
        const response = await fetch(url, {
          method,
          headers: requestHeaders,
          body: bodyStr || undefined,
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          throw new Error(`API error: ${response.status} ${errorText}`);
        }

        return response;
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Check if it's a CORS error when using direct connection
      if (
        !authState.useRelay &&
        error instanceof TypeError &&
        error.message.includes('Failed to fetch')
      ) {
        throw new Error(
          'CORS error: Direct connection blocked. Please use "Connect Through Server" mode or enable CORS on Kalshi API.'
        );
      }

      // Exponential backoff with jitter
      if (attempt < 2) {
        const delay = Math.pow(2, attempt) * 1000 + Math.random() * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Request failed after retries');
}

/**
 * Check if error is CORS-related
 * Note: With relay, CORS errors should be rare, but kept for compatibility
 */
export function isCorsError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.message === 'CORS_BLOCKED' ||
      error.message.includes('CORS') ||
      (error.message.includes('Failed to fetch') && !error.message.includes('Relay'))
    );
  }
  return false;
}
